/**
 * API Service Layer - Phase 3 Complete
 * Centralized API calls with authentication and error handling
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.fly8.com/api/v1';
const REFRESH_URL = `${API_BASE_URL}/auth/refresh`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Required to send the HttpOnly refresh token cookie
  headers: {
    'Content-Type': 'application/json'
  }
});

// --- Silent refresh state ---
let isRefreshing = false;
let failedQueue = []; // Holds requests that arrived while a refresh was in-flight

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('student');
  window.location.href = '/login';
};

// Request interceptor - attach current access token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - on 401, attempt silent refresh then retry original request
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only intercept genuine 401s; skip the refresh endpoint itself to avoid loops
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is already in flight — queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use raw axios so this call does NOT pass through the interceptor again
      const { data } = await axios.post(REFRESH_URL, {}, { withCredentials: true });
      const newToken = data.token;

      localStorage.setItem('token', newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      processQueue(null, newToken);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ============================================
// AUTH API
// ============================================

export const authAPI = {
  login: async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.student) {
        localStorage.setItem('student', JSON.stringify(data.student));
      }
    }
    return data;
  },

  signup: async (userData) => {
    const { data } = await api.post('/auth/signup', userData);
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  getMe: async () => {
    const { data } = await api.get('/auth/me');
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.student) {
        localStorage.setItem('student', JSON.stringify(data.student));
      }
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('student');
    window.location.href = '/login';
  },

  changeEmail: async (newEmail, password) => {
    const { data } = await api.put('/auth/change-email', { newEmail, password });
    return data;
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const { data } = await api.put('/auth/change-password', { currentPassword, newPassword, confirmPassword });
    return data;
  },

  deactivateAccount: async () => {
    const { data } = await api.put('/auth/deactivate');
    return data;
  },

  deleteAccount: async () => {
    const { data } = await api.delete('/auth/account');
    return data;
  }
};

// ============================================
// SERVICE REQUEST API
// ============================================

export const serviceRequestAPI = {
  create: async (serviceType, metadata = {}, formData = null, representativeName) => {
    const payload = { serviceType, metadata };
    if (formData) {
      payload.formData = formData;
    }
    if (typeof representativeName === 'string' && representativeName.trim()) {
      payload.representativeName = representativeName.trim();
    }
    const { data } = await api.post('/service-requests', payload);
    return data;
  },

  getAll: async (params = {}) => {
    const { data } = await api.get('/service-requests', { params });
    return data;
  },

  getById: async (serviceRequestId) => {
    const { data } = await api.get(`/service-requests/${serviceRequestId}`);
    return data;
  },

  assign: async (serviceRequestId, assignedCounselor, assignedAgent, note) => {
    const { data } = await api.post(`/service-requests/${serviceRequestId}/assign`, {
      assignedCounselor,
      assignedAgent,
      note
    });
    return data;
  },

  updateStatus: async (serviceRequestId, status, note) => {
    const { data } = await api.patch(`/service-requests/${serviceRequestId}/status`, { status, note });
    return data;
  },

  addNote: async (serviceRequestId, text, isInternal = false) => {
    const { data } = await api.post(`/service-requests/${serviceRequestId}/notes`, { text, isInternal });
    return data;
  },

  getStats: async () => {
    const { data } = await api.get('/service-requests/stats');
    return data;
  }
};

// ============================================
// TASK API
// ============================================

export const taskAPI = {
  create: async (taskData) => {
    const { data } = await api.post('/tasks', taskData);
    return data;
  },

  getAll: async (params = {}) => {
    const { data } = await api.get('/tasks', { params });
    return data;
  },

  getById: async (taskId) => {
    const { data } = await api.get(`/tasks/${taskId}`);
    return data;
  },

  submit: async (taskId, text, files = []) => {
    const { data } = await api.post(`/tasks/${taskId}/submit`, { text, files });
    return data;
  },

  review: async (taskId, feedback, rating, requiresRevision) => {
    const { data } = await api.post(`/tasks/${taskId}/review`, { feedback, rating, requiresRevision });
    return data;
  },

  updateStatus: async (taskId, status, note) => {
    const { data } = await api.patch(`/tasks/${taskId}/status`, { status, note });
    return data;
  },

  delete: async (taskId) => {
    const { data } = await api.delete(`/tasks/${taskId}`);
    return data;
  },

  getStats: async (serviceRequestId) => {
    const { data } = await api.get(`/tasks/stats/${serviceRequestId}`);
    return data;
  }
};

// ============================================
// NOTIFICATION API
// ============================================

export const notificationAPI = {
  getAll: async (params = {}) => {
    const { data } = await api.get('/notifications', { params });
    return data;
  },

  getUnreadCount: async () => {
    const { data } = await api.get('/notifications/unread-count');
    return data;
  },

  markAsRead: async (notificationId) => {
    const { data } = await api.patch(`/notifications/${notificationId}/read`);
    return data;
  },

  markAllAsRead: async () => {
    const { data } = await api.patch('/notifications/mark-all-read');
    return data;
  }
};

// ============================================
// ADMIN NOTIFICATION API
// ============================================

export const adminNotificationAPI = {
  // Get all notifications (admin view with pagination)
  getAll: async (params = {}) => {
    const { data } = await api.get('/admin/notifications', { params });
    return data;
  },

  // Get notification statistics
  getStats: async () => {
    const { data } = await api.get('/admin/notifications/stats');
    return data;
  },

  // Get single notification by ID
  getById: async (notificationId) => {
    const { data } = await api.get(`/admin/notifications/${notificationId}`);
    return data;
  },

  // Create and send new notification
  create: async (notificationData) => {
    const { data } = await api.post('/admin/notifications', notificationData);
    return data;
  },

  // Archive a notification
  archive: async (notificationId) => {
    const { data } = await api.patch(`/admin/notifications/${notificationId}`, { isArchived: true });
    return data;
  },

  // Unarchive a notification
  unarchive: async (notificationId) => {
    const { data } = await api.patch(`/admin/notifications/${notificationId}`, { isArchived: false });
    return data;
  },

  // Delete a notification
  delete: async (notificationId) => {
    const { data } = await api.delete(`/admin/notifications/${notificationId}`);
    return data;
  },

  // Bulk action (archive, unarchive, delete)
  bulkAction: async (notificationIds, action) => {
    const { data } = await api.post('/admin/notifications/bulk', { notificationIds, action });
    return data;
  },

  // Get available recipients for notification targeting
  getRecipients: async (params = {}) => {
    const { data } = await api.get('/admin/users/recipients', { params });
    return data;
  }
};

// ============================================
// SERVICES API
// ============================================

export const servicesAPI = {
  getAll: async (params = {}) => {
    const { data } = await api.get('/services', { params });
    return data;
  }
};

// ============================================
// STUDENT API
// ============================================

export const studentAPI = {
  // Get profile by ID (admin/counselor view)
  getProfile: async (studentId) => {
    const { data } = await api.get(`/students/${studentId}`);
    return data;
  },

  // Get authenticated student's own profile
  getMyProfile: async () => {
    const { data } = await api.get('/students/profile');
    return data;
  },

  // Update authenticated student's profile
  updateMyProfile: async (profileData) => {
    const { data } = await api.put('/students/profile', profileData);
    return data;
  },

  // Legacy method - kept for backward compatibility
  updateProfile: async (studentId, profileData) => {
    const { data } = await api.patch(`/students/${studentId}`, profileData);
    return data;
  },

  // Upload profile image/avatar
  uploadProfileImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post('/students/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  // Upload document (transcripts, resume, etc.)
  uploadDocument: async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const { data } = await api.post('/students/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  // Get all uploaded documents
  getDocuments: async () => {
    const { data } = await api.get('/students/documents');
    return data;
  },

  // Delete a document
  deleteDocument: async (documentType) => {
    const { data } = await api.delete(`/students/documents/${documentType}`);
    return data;
  },

  getMyApplications: async () => {
    const { data } = await api.get('/students/my-applications');
    return data;
  },

  applyServices: async (serviceIds) => {
    const { data } = await api.post('/students/apply-services', { serviceIds });
    return data;
  },

  completeOnboarding: async (onboardingData) => {
    const { data } = await api.post('/students/onboarding', onboardingData);
    return data;
  }
};

// ============================================
// ADMIN API
// ============================================

export const adminAPI = {
  getStudents: async (params = {}) => {
    const { data } = await api.get('/admin/students', { params });
    return data;
  },

  // Payment Requests
  listPaymentRequests: async (params = {}) => {
    const { data } = await api.get('/admin/payment-requests', { params });
    return data;
  },

  approvePaymentRequest: async (id) => {
    const { data } = await api.patch(`/admin/payment-requests/${id}/approve`);
    return data;
  },

  markPaymentRequestPaid: async (id, payload = {}) => {
    const { data } = await api.patch(`/admin/payment-requests/${id}/pay`, payload);
    return data;
  },

  rejectPaymentRequest: async (id, reason = '') => {
    const { data } = await api.patch(`/admin/payment-requests/${id}/reject`, { reason });
    return data;
  },

  // Counselor Management
  getCounselors: async (params = {}) => {
    const { data } = await api.get('/admin/counselors', { params });
    return data;
  },

  getCounselorById: async (counselorId) => {
    const { data } = await api.get(`/admin/counselors/${counselorId}`);
    return data;
  },

  createCounselor: async (counselorData) => {
    const { data } = await api.post('/admin/counselors', counselorData);
    return data;
  },

  updateCounselor: async (counselorId, counselorData) => {
    const { data } = await api.put(`/admin/counselors/${counselorId}`, counselorData);
    return data;
  },

  updateCounselorStatus: async (counselorId, isActive) => {
    const { data } = await api.patch(`/admin/counselors/${counselorId}/status`, { isActive });
    return data;
  },

  deleteCounselor: async (counselorId, permanent = false) => {
    const { data } = await api.delete(`/admin/counselors/${counselorId}?permanent=${permanent}`);
    return data;
  },

  getCounselorStats: async () => {
    const { data } = await api.get('/admin/counselors/stats');
    return data;
  },

  resetCounselorPassword: async (counselorId, newPassword) => {
    const { data } = await api.post(`/admin/counselors/${counselorId}/reset-password`, { newPassword });
    return data;
  },

  // Agent Management
  getAgents: async (params = {}) => {
    const { data } = await api.get('/admin/agents', { params });
    return data;
  },

  getAgentById: async (agentId) => {
    const { data } = await api.get(`/admin/agents/${agentId}`);
    return data;
  },

  createAgent: async (agentData) => {
    const { data } = await api.post('/admin/agents', agentData);
    return data;
  },

  updateAgent: async (agentId, agentData) => {
    const { data } = await api.put(`/admin/agents/${agentId}`, agentData);
    return data;
  },

  updateAgentStatus: async (agentId, isActive) => {
    const { data } = await api.patch(`/admin/agents/${agentId}/status`, { isActive });
    return data;
  },

  updateAgentCommission: async (agentId, commissionPercentage) => {
    const { data } = await api.patch(`/admin/agents/${agentId}/commission`, { commissionPercentage });
    return data;
  },

  // Commission Management
  approveCommission: async (commissionId) => {
    const { data } = await api.put(`/admin/commissions/${commissionId}/approve`);
    return data;
  },

  rejectCommission: async (commissionId, reason) => {
    const { data } = await api.put(`/admin/commissions/${commissionId}/reject`, { reason });
    return data;
  },

  processCommissionPayout: async (commissionId, externalReference) => {
    const { data } = await api.post(`/admin/commissions/${commissionId}/payout`, { externalReference });
    return data;
  },

  createCommission: async (commissionData) => {
    const { data } = await api.post('/admin/commissions/create', commissionData);
    return data;
  },

  bulkApproveCommissions: async (commissionIds) => {
    const { data } = await api.post('/admin/commissions/bulk-approve', { commissionIds });
    return data;
  },

  // Payout Management
  getPayoutRequests: async (params = {}) => {
    const { data } = await api.get('/admin/payouts', { params });
    return data;
  },

  processPayout: async (payoutId, externalReference, note) => {
    const { data } = await api.post(`/admin/payouts/${payoutId}/process`, { externalReference, note });
    return data;
  },

  rejectPayout: async (payoutId, reason) => {
    const { data } = await api.post(`/admin/payouts/${payoutId}/reject`, { reason });
    return data;
  },

  deleteAgent: async (agentId, permanent = false) => {
    const { data } = await api.delete(`/admin/agents/${agentId}?permanent=${permanent}`);
    return data;
  },

  getAgentStats: async () => {
    const { data } = await api.get('/admin/agents/stats');
    return data;
  },

  resetAgentPassword: async (agentId, newPassword) => {
    const { data } = await api.post(`/admin/agents/${agentId}/reset-password`, { newPassword });
    return data;
  },

  // Dashboard Metrics
  getMetrics: async () => {
    const { data } = await api.get('/admin/metrics');
    return data;
  },

  getStatistics: async () => {
    const { data } = await api.get('/admin/statistics');
    return data;
  },

  getPendingAssignments: async () => {
    const { data } = await api.get('/admin/pending-assignments');
    return data;
  },

  // Service Requests
  getAllServiceRequests: async (params = {}) => {
    const { data } = await api.get('/admin/service-requests', { params });
    return data;
  },

  assignServiceRequest: async (requestId, assignmentData) => {
    const { data } = await api.post(`/admin/service-requests/${requestId}/assign`, assignmentData);
    return data;
  },

  updateServiceRequestStatus: async (requestId, statusData) => {
    const { data } = await api.patch(`/admin/service-requests/${requestId}/status`, statusData);
    return data;
  },

  deleteServiceRequest: async (requestId) => {
    const { data } = await api.delete(`/admin/service-requests/${requestId}`);
    return data;
  },

  // ===========================================
  // AGENT SERVICE REQUEST APPROVAL API
  // ===========================================

  // Get all agent-initiated service requests
  getAgentRequests: async (params = {}) => {
    const { data } = await api.get('/admin/agent-requests', { params });
    return data;
  },

  // Get pending agent requests count (for dashboard badge)
  getAgentRequestsPendingCount: async () => {
    const { data } = await api.get('/admin/agent-requests/pending-count');
    return data;
  },

  // Approve an agent service request
  approveAgentRequest: async (requestId, notes = '') => {
    const { data } = await api.post(`/admin/agent-requests/${requestId}/approve`, { notes });
    return data;
  },

  // Reject an agent service request
  rejectAgentRequest: async (requestId, reason) => {
    const { data } = await api.post(`/admin/agent-requests/${requestId}/reject`, { reason });
    return data;
  },

  // ===========================================
  // REPRESENTATIVE MANAGEMENT API
  // ===========================================

  getRepresentatives: async (params = {}) => {
    const { data } = await api.get('/admin/representatives', { params });
    return data;
  },

  getRepresentativeById: async (userId) => {
    const { data } = await api.get(`/admin/representatives/${userId}`);
    return data;
  },

  createRepresentative: async (repData) => {
    const { data } = await api.post('/admin/representatives', repData);
    return data;
  },

  updateRepresentative: async (userId, repData) => {
    const { data } = await api.put(`/admin/representatives/${userId}`, repData);
    return data;
  },

  updateRepresentativeStatus: async (userId, isActive) => {
    const { data } = await api.patch(`/admin/representatives/${userId}/status`, { isActive });
    return data;
  },

  updateRepresentativeCommission: async (userId, commissionPercentage) => {
    const { data } = await api.patch(`/admin/representatives/${userId}/commission`, { commissionPercentage });
    return data;
  },

  deleteRepresentative: async (userId, permanent = false) => {
    const { data } = await api.delete(`/admin/representatives/${userId}?permanent=${permanent}`);
    return data;
  },

  getRepresentativeStats: async () => {
    const { data } = await api.get('/admin/representatives/stats');
    return data;
  },

  resetRepresentativePassword: async (userId, newPassword) => {
    const { data } = await api.post(`/admin/representatives/${userId}/reset-password`, { newPassword });
    return data;
  },

  getRepresentativeCommissions: async (userId, params = {}) => {
    const { data } = await api.get(`/admin/representatives/${userId}/commissions`, { params });
    return data;
  },

  // ===========================================
  // STUDENT MANAGEMENT API
  // ===========================================

  // Get student by ID with full details
  getStudentById: async (studentId) => {
    const { data } = await api.get(`/admin/students/${studentId}`);
    return data;
  },

  // Update student profile
  updateStudent: async (studentId, profileData) => {
    const { data } = await api.put(`/admin/students/${studentId}`, profileData);
    return data;
  },

  // Get student notes
  getStudentNotes: async (studentId) => {
    const { data } = await api.get(`/admin/students/${studentId}/notes`);
    return data;
  },

  // Add note to student
  addStudentNote: async (studentId, text) => {
    const { data } = await api.post(`/admin/students/${studentId}/notes`, { text });
    return data;
  },

  // Assign agent to student
  assignStudentAgent: async (studentId, agentId) => {
    const { data } = await api.put(`/admin/students/${studentId}/assign-agent`, { agentId });
    return data;
  },

  // Assign counselor to student
  assignStudentCounselor: async (studentId, counselorId) => {
    const { data } = await api.put(`/admin/students/${studentId}/assign-counselor`, { counselorId });
    return data;
  },

  // Upload document for student
  uploadStudentDocument: async (studentId, file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    const { data } = await api.post(`/admin/students/${studentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  // Delete student document
  deleteStudentDocument: async (studentId, documentType) => {
    const { data } = await api.delete(`/admin/students/${studentId}/documents/${documentType}`);
    return data;
  },

  // Update service request progress
  updateServiceRequestProgress: async (requestId, progress, note = '') => {
    const { data } = await api.patch(`/admin/service-requests/${requestId}/progress`, { progress, note });
    return data;
  },

  // Update service request priority
  updateServiceRequestPriority: async (requestId, priority) => {
    const { data } = await api.patch(`/admin/service-requests/${requestId}/priority`, { priority });
    return data;
  },

  // Update service request deadline
  updateServiceRequestDeadline: async (requestId, deadline) => {
    const { data } = await api.patch(`/admin/service-requests/${requestId}/deadline`, { deadline });
    return data;
  }
};

// ============================================
// COUNSELOR API
// ============================================

// counselorAPI removed — counselors now use agentAPI (backend agents.js accepts both roles)

// ============================================
// AGENT API
// ============================================

export const agentAPI = {
  getDashboard: async () => {
    const { data } = await api.get('/agents/dashboard');
    return data;
  },

  getStudents: async (params = {}) => {
    const { data } = await api.get('/agents/students', { params });
    return data;
  },

  getMyStudents: async (params = {}) => {
    const { data } = await api.get('/agents/my-students', { params });
    return data;
  },

  getStudentsList: async () => {
    const { data } = await api.get('/agents/students/list');
    return data;
  },

  referStudent: async (studentData) => {
    const { data } = await api.post('/agents/refer-student', studentData);
    return data;
  },

  getCommissions: async (params = {}) => {
    const { data } = await api.get('/agents/commissions', { params });
    return data;
  },

  getCommissionStats: async () => {
    const { data } = await api.get('/agents/commission-stats');
    return data;
  },

  getCommissionHistory: async (params = {}) => {
    const { data } = await api.get('/agents/commission-history', { params });
    return data;
  },

  getEarnings: async () => {
    const { data } = await api.get('/agents/earnings');
    return data;
  },

  // Wallet & Payouts
  getWallet: async () => {
    const { data } = await api.get('/agents/wallet');
    return data;
  },

  requestPayout: async (amount, note) => {
    const { data } = await api.post('/agents/payouts/request', { amount, note });
    return data;
  },

  getPayouts: async (params = {}) => {
    const { data } = await api.get('/agents/payouts', { params });
    return data;
  },

  exportCommissions: async (params = {}) => {
    const response = await api.get('/agents/commissions/export', {
      params,
      responseType: 'blob'
    });
    return response;
  },

  // Legacy service requests endpoint
  getServiceRequests: async (params = {}) => {
    const { data } = await api.get('/agents/service-requests', { params });
    return data;
  },

  addNote: async (requestId, text, isInternal = false) => {
    const { data } = await api.post(`/agents/service-requests/${requestId}/notes`, { text, isInternal });
    return data;
  },

  updateStatus: async (requestId, status, note = '') => {
    const { data } = await api.patch(`/agents/service-requests/${requestId}/progress`, { status, note });
    return data;
  },

  // ===========================================
  // CASE PIPELINE API (Industrial-Grade)
  // ===========================================

  // Get pipeline view with cases grouped by status
  getCasePipeline: async () => {
    const { data } = await api.get('/agents/cases/pipeline');
    return data;
  },

  // Get full case details (Case File)
  getCaseById: async (caseId) => {
    const { data } = await api.get(`/agents/cases/${caseId}`);
    return data;
  },

  // Update case status
  updateCaseStatus: async (caseId, status, note = '') => {
    const { data } = await api.patch(`/agents/cases/${caseId}/status`, { status, note });
    return data;
  },

  // Update case progress
  updateCaseProgress: async (caseId, progress, note = '') => {
    const { data } = await api.patch(`/agents/cases/${caseId}/progress`, { progress, note });
    return data;
  },

  // Update case deadline
  updateCaseDeadline: async (caseId, deadline, note = '') => {
    const { data } = await api.patch(`/agents/cases/${caseId}/deadline`, { deadline, note });
    return data;
  },

  // Update case priority
  updateCasePriority: async (caseId, priority, note = '') => {
    const { data } = await api.patch(`/agents/cases/${caseId}/priority`, { priority, note });
    return data;
  },

  // Add note to case (using existing endpoint)
  addCaseNote: async (caseId, text, isInternal = false) => {
    const { data } = await api.post(`/agents/service-requests/${caseId}/notes`, { text, isInternal });
    return data;
  },

  // ===========================================
  // WORK QUEUE API (Tasks)
  // ===========================================

  // Get work queue (all tasks from all cases)
  getWorkQueue: async (params = {}) => {
    const { data } = await api.get('/agents/workqueue', { params });
    return data;
  },

  // Create task for a case
  createCaseTask: async (caseId, taskData) => {
    const { data } = await api.post(`/agents/cases/${caseId}/tasks`, taskData);
    return data;
  },

  // Mark task as complete
  completeTask: async (taskId, note = '') => {
    const { data } = await api.patch(`/agents/tasks/${taskId}/complete`, { note });
    return data;
  },

  // ===========================================
  // STUDENT PROFILE MANAGEMENT API
  // ===========================================

  // Get full student details with aggregated metrics
  getStudentDetails: async (studentId) => {
    const { data } = await api.get(`/agents/students/${studentId}`);
    return data;
  },

  // Update student profile
  updateStudent: async (studentId, profileData) => {
    const { data } = await api.put(`/agents/students/${studentId}`, profileData);
    return data;
  },

  // Apply service for student
  applyServiceForStudent: async (studentId, serviceType) => {
    const { data } = await api.post(`/agents/students/${studentId}/apply-service`, { serviceType });
    return data;
  },

  // Get student notes
  getStudentNotes: async (studentId) => {
    const { data } = await api.get(`/agents/students/${studentId}/notes`);
    return data;
  },

  // Add student note
  addStudentNote: async (studentId, text) => {
    const { data } = await api.post(`/agents/students/${studentId}/notes`, { text });
    return data;
  },

  // Upload student document
  uploadStudentDocument: async (studentId, file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    const { data } = await api.post(`/agents/students/${studentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  // Delete student document
  deleteStudentDocument: async (studentId, documentType) => {
    const { data } = await api.delete(`/agents/students/${studentId}/documents/${documentType}`);
    return data;
  },

  // ===========================================
  // EXPLORE / SERVICE MARKETPLACE API
  // ===========================================

  // Get service request statuses for explore page
  getExploreServiceStatus: async () => {
    const { data } = await api.get('/agents/explore/service-status');
    return data;
  },

  // Submit service referral from explore page
  referService: async (studentId, serviceType, notes, documents) => {
    const formData = new FormData();
    formData.append('studentId', studentId);
    formData.append('serviceType', serviceType);
    if (notes) formData.append('notes', notes);
    if (documents && documents.length > 0) {
      documents.forEach(file => formData.append('documents', file));
    }
    const { data } = await api.post('/agents/explore/refer-service', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  // ===========================================
  // COURSE & PROGRAM DISCOVERY API
  // ===========================================

  searchCourses: async (params = {}) => {
    const { data } = await api.get('/agents/courses/search', { params });
    return data;
  },

  getCourseFilters: async () => {
    const { data } = await api.get('/agents/courses/filters');
    return data;
  },

  getCourseDetail: async (id) => {
    const { data } = await api.get(`/agents/courses/${id}`);
    return data;
  },

  addToShortlist: async (payload) => {
    const { data } = await api.post('/agents/courses/shortlist', payload);
    return data;
  },

  removeFromShortlist: async (shortlistId) => {
    const { data } = await api.delete(`/agents/courses/shortlist/${shortlistId}`);
    return data;
  },

  getShortlists: async (params = {}) => {
    const { data } = await api.get('/agents/courses/shortlists', { params });
    return data;
  }
};

// ============================================
// UPLOAD API
// ============================================

export const uploadAPI = {
  uploadFile: async (file, folder = 'documents') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const { data } = await api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    // Backend returns { message, file: { url, publicId, name, size, format } }.
    // Flatten so callers can read .url / .publicId directly (legacy contract).
    const f = data.file || {};
    return { ...data, ...f };
  },

  uploadMultiple: async (files, folder = 'documents') => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('folder', folder);

    const { data } = await api.post('/upload/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  deleteFile: async (publicId) => {
    const { data } = await api.delete('/upload/file', { data: { publicId } });
    return data;
  },

  // Build a download URL that proxies through the backend so Cloudinary
  // returns a signed attachment (bypasses the /image/upload/*.pdf 401 block).
  getDownloadUrl: (url, name) => {
    if (!url) return url;
    const qs = new URLSearchParams({ url });
    if (name) qs.set('name', name);
    return `${API_BASE_URL}/upload/download?${qs.toString()}`;
  }
};

// ============================================
// CHAT API
// ============================================

export const chatAPI = {
  getMessages: async (serviceRequestId, params = {}) => {
    const { data } = await api.get(`/chat/${serviceRequestId}/messages`, { params });
    return data;
  },

  sendMessage: async (serviceRequestId, content, attachments = [], messageType = 'TEXT') => {
    const { data } = await api.post(`/chat/${serviceRequestId}/messages`, {
      content,
      attachments,
      messageType
    });
    return data;
  },

  markAsRead: async (serviceRequestId, messageId) => {
    const { data } = await api.patch(`/chat/${serviceRequestId}/messages/${messageId}/read`);
    return data;
  },

  markAllAsRead: async (serviceRequestId) => {
    const { data } = await api.patch(`/chat/${serviceRequestId}/messages/read-all`);
    return data;
  },

  getUnreadCount: async (serviceRequestId) => {
    const { data } = await api.get(`/chat/${serviceRequestId}/unread-count`);
    return data;
  },

  getParticipants: async (serviceRequestId) => {
    const { data } = await api.get(`/chat/${serviceRequestId}/participants`);
    return data;
  }
};

// ============================================
// REPORTS API (Super Admin)
// ============================================

export const reportsAPI = {
  // Get overview KPIs and summary statistics
  getOverview: async (params = {}) => {
    const { data } = await api.get('/admin/reports/overview', { params });
    return data;
  },

  // Get applications with filters and pagination
  getApplications: async (params = {}) => {
    const { data } = await api.get('/admin/reports/applications', { params });
    return data;
  },

  // Get country-wise application breakdown
  getApplicationsByCountry: async (params = {}) => {
    const { data } = await api.get('/admin/reports/applications/by-country', { params });
    return data;
  },

  // Get university-wise application breakdown
  getApplicationsByUniversity: async (params = {}) => {
    const { data } = await api.get('/admin/reports/applications/by-university', { params });
    return data;
  },

  // Get commission/revenue reports
  getCommissions: async (params = {}) => {
    const { data } = await api.get('/admin/reports/commissions', { params });
    return data;
  },

  // Get agent performance metrics
  getAgentPerformance: async (params = {}) => {
    const { data } = await api.get('/admin/reports/agents/performance', { params });
    return data;
  },

  // Get monthly trends for a specific year
  getMonthlyTrends: async (params = {}) => {
    const { data } = await api.get('/admin/reports/trends/monthly', { params });
    return data;
  },

  // Get yearly comparison data
  getYearlyTrends: async (params = {}) => {
    const { data } = await api.get('/admin/reports/trends/yearly', { params });
    return data;
  },

  // Get list of countries with students
  getCountries: async () => {
    const { data } = await api.get('/admin/reports/countries');
    return data;
  }
};

// ============================================
// SETTINGS API (Super Admin)
// ============================================

export const settingsAPI = {
  // Get all platform settings
  getAll: async () => {
    const { data } = await api.get('/admin/settings');
    return data;
  },

  // Get specific settings section
  getSection: async (section) => {
    const { data } = await api.get(`/admin/settings/${section}`);
    return data;
  },

  // Update all settings
  updateAll: async (settings) => {
    const { data } = await api.put('/admin/settings', settings);
    return data;
  },

  // Update specific settings section
  updateSection: async (section, settings) => {
    const { data } = await api.put(`/admin/settings/${section}`, settings);
    return data;
  },

  // Reset settings to defaults
  reset: async (section = null) => {
    const { data } = await api.post('/admin/settings/reset', { section });
    return data;
  },

  // Get settings change history
  getHistory: async (params = {}) => {
    const { data } = await api.get('/admin/settings/audit/history', { params });
    return data;
  },

  // Test email configuration
  testEmail: async (email) => {
    const { data } = await api.post('/admin/settings/test-email', { email });
    return data;
  },

  // Get available countries list
  getCountriesList: async () => {
    const { data } = await api.get('/admin/settings/countries/list');
    return data;
  },

  // Upload platform logo
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const { data } = await api.post('/admin/settings/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  }
};

// ============================================
// ADMISSIONS API (University Applications)
// ============================================

export const admissionsAPI = {
  // --- Agent ---
  create: async (data) => {
    const { data: res } = await api.post('/admissions/agent/create', data);
    return res;
  },
  getAgentList: async (params) => {
    const { data: res } = await api.get('/admissions/agent', { params });
    return res;
  },
  getById: async (id) => {
    const { data: res } = await api.get(`/admissions/agent/${id}`);
    return res;
  },
  updateStatus: async (id, data) => {
    const { data: res } = await api.patch(`/admissions/agent/${id}/status`, data);
    return res;
  },
  uploadDoc: async (id, formData) => {
    const { data: res } = await api.post(`/admissions/agent/${id}/upload-doc`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res;
  },
  addRemark: async (id, data) => {
    const { data: res } = await api.post(`/admissions/agent/${id}/remark`, data);
    return res;
  },
  updateChecklist: async (id, data) => {
    const { data: res } = await api.patch(`/admissions/agent/${id}/checklist`, data);
    return res;
  },

  // --- Student ---
  getStudentList: async (params) => {
    const { data: res } = await api.get('/admissions/student', { params });
    return res;
  },
  getStudentById: async (id) => {
    const { data: res } = await api.get(`/admissions/student/${id}`);
    return res;
  },
  studentUploadDoc: async (id, formData) => {
    const { data: res } = await api.post(`/admissions/student/${id}/upload-doc`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res;
  },
  acceptOffer: async (id) => {
    const { data: res } = await api.post(`/admissions/student/${id}/accept-offer`);
    return res;
  },

  // --- Admin ---
  assign: async (data) => {
    const { data: res } = await api.post('/admissions/admin/assign', data);
    return res;
  },
  getAdminList: async (params) => {
    const { data: res } = await api.get('/admissions/admin', { params });
    return res;
  },
  getAdminById: async (id) => {
    const { data: res } = await api.get(`/admissions/admin/${id}`);
    return res;
  },
  adminUpdateStatus: async (id, data) => {
    const { data: res } = await api.patch(`/admissions/admin/${id}/status`, data);
    return res;
  },
  adminUploadDoc: async (id, formData) => {
    const { data: res } = await api.post(`/admissions/admin/${id}/upload-doc`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res;
  },
  adminAddRemark: async (id, data) => {
    const { data: res } = await api.post(`/admissions/admin/${id}/remark`, data);
    return res;
  },
  adminUpdateChecklist: async (id, data) => {
    const { data: res } = await api.patch(`/admissions/admin/${id}/checklist`, data);
    return res;
  }
};

// ============================================
// CAMPAIGN LEADS API (Super Admin)
// ============================================

export const campaignLeadsAPI = {
  // GET /admin/campaign-leads/stats — total count per service type (global)
  getStats: async () => {
    const { data } = await api.get('/admin/campaign-leads/stats');
    return data;
  },

  // GET /admin/campaign-leads — paginated list with filters
  getLeads: async (params = {}) => {
    const { data } = await api.get('/admin/campaign-leads', { params });
    return data;
  },

  // GET /admin/campaign-leads/:id — single lead detail
  getLeadById: async (id) => {
    const { data } = await api.get(`/admin/campaign-leads/${id}`);
    return data;
  },

  // PATCH /admin/campaign-leads/:id — update status / admin notes
  updateLead: async (id, updates) => {
    const { data } = await api.patch(`/admin/campaign-leads/${id}`, updates);
    return data;
  },

  // DELETE /admin/campaign-leads/:id — permanently delete a lead
  deleteLead: async (id) => {
    const { data } = await api.delete(`/admin/campaign-leads/${id}`);
    return data;
  },

  // GET /admin/campaign-leads/export — download Excel file (returns raw response)
  exportLeads: async (params = {}) => {
    const response = await api.get('/admin/campaign-leads/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },
};

// Public program API — no auth required, used by Apply University page
export const publicProgramAPI = {
  getById: async (programId) => {
    const { data } = await api.get(`/public/programs/${programId}`);
    return data;
  },
};

// ============================================
// REPRESENTATIVE API (Level 1 & 2 - Read Only)
// ============================================

export const representativeAPI = {
  getDashboard: async () => {
    const { data } = await api.get('/representatives/dashboard');
    return data;
  },

  getStudents: async (params = {}) => {
    const { data } = await api.get('/representatives/students', { params });
    return data;
  },

  getServiceRequests: async (params = {}) => {
    const { data } = await api.get('/representatives/service-requests', { params });
    return data;
  },

  getServiceRequestById: async (id) => {
    const { data } = await api.get(`/representatives/service-requests/${id}`);
    return data;
  },

  getCommissions: async (params = {}) => {
    const { data } = await api.get('/representatives/commissions', { params });
    return data;
  },

  getCommissionStats: async () => {
    const { data } = await api.get('/representatives/commission-stats');
    return data;
  }
};

// ============================================
// PARTNER API (Level 3 - Full Operator)
// ============================================

export const partnerAPI = {
  getDashboard: async () => {
    const { data } = await api.get('/partners/dashboard');
    return data;
  },

  // Student Management
  createStudent: async (studentData) => {
    const { data } = await api.post('/partners/students', studentData);
    return data;
  },

  getStudents: async (params = {}) => {
    const { data } = await api.get('/partners/students', { params });
    return data;
  },

  getStudentDetails: async (studentId) => {
    const { data } = await api.get(`/partners/students/${studentId}`);
    return data;
  },

  // Service Requests
  createServiceRequest: async (requestData) => {
    const { data } = await api.post('/partners/service-requests', requestData);
    return data;
  },

  getServiceRequests: async (params = {}) => {
    const { data } = await api.get('/partners/service-requests', { params });
    return data;
  },

  getServiceRequestById: async (id) => {
    const { data } = await api.get(`/partners/service-requests/${id}`);
    return data;
  },

  // Tasks
  getTasks: async (params = {}) => {
    const { data } = await api.get('/partners/tasks', { params });
    return data;
  },

  submitTask: async (taskId, text, files = []) => {
    const { data } = await api.post(`/partners/tasks/${taskId}/submit`, { text, files });
    return data;
  },

  // Commissions
  getCommissions: async (params = {}) => {
    const { data } = await api.get('/partners/commissions', { params });
    return data;
  },

  getCommissionStats: async () => {
    const { data } = await api.get('/partners/commission-stats');
    return data;
  },

  // Payment Requests
  listPaymentRequests: async (params = {}) => {
    const { data } = await api.get('/partners/payment-requests', { params });
    return data;
  },

  createPaymentRequest: async (payload) => {
    const { data } = await api.post('/partners/payment-requests', payload);
    return data;
  },

  cancelPaymentRequest: async (id) => {
    const { data } = await api.patch(`/partners/payment-requests/${id}/cancel`);
    return data;
  },
};

export default api;
